import type { MultiStepCompleteEvent } from './multi-step-form.ts'

type Stage = {
	title: string
	name: string
	required: number
	fields: { value: string }[]
}

type JobImage = {
	src: string
	srcset: string
	alt: string
	style: string
	x: number
	y: number
} | null

type Job = {
	title: string
	url: string
	types: string[]
	locations: string[]
	scores: Record<string, Record<string, number>>
	description: string
	image: JobImage
}

type Question = {
	stageTitle: string
	label: string
}

type MatchItem = {
	job: Job
	score: number
}

type Config = {
	stages: Stage[]
	jobs: Job[]
    applyLink: string | null
    applyTextMap: Record<string, string>
    location: string
}

type Values = Record<string, string | string[]>

export default function init() {
    const forms = [...document.querySelectorAll('form.js-skills-matcher-form')] as HTMLFormElement[]
    forms.map(skillsMatcher)
}

function skillsMatcher(form: HTMLFormElement): void {
	if (!form) return

	const configEl = document.getElementById('skills-matcher-data')
	const config = (configEl ? JSON.parse(configEl.textContent) : {}) as Config
	const stages = config.stages || []
	const jobs = config.jobs || []
	const limit = 8
	let programme = ''

	const resultsList = form.querySelector<HTMLUListElement>('.js-skills-matcher-results')
    const resultsViewAll = form.querySelector<HTMLElement>('.js-skills-matcher-results__view-all')
    const resultsContentMatches = form.querySelector<HTMLElement>('.skills-matcher-form__results__matches')
    const resultsContentBottom = form.querySelector<HTMLElement>('.skills-matcher-form__results__bottom')
    const resultsContentNoMatches = form.querySelector<HTMLElement>('.skills-matcher-form__results__no-matches')

	if (!resultsList) return

	form.addEventListener('click', event => {
		const btn = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-step-select]')
		if (btn) programme = btn.value

		const completeBtn = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-step-complete]')
		if (completeBtn) programme = ''
	}, { capture: true })

	form.addEventListener('multistep:complete', (event) => {
		const detail = (event as CustomEvent<MultiStepCompleteEvent>).detail

		const matches = matchJobs(detail.values, stages, jobs, programme, config.location)
            .slice(0, 5)

        if (matches.length) {
            resultsList.style.display = ''
            let html = ''
            matches.map((item, i) => {
                if (i > 3 && item.score < 0.3) return
                html += renderJob(item, i)
                
            })
            resultsList.innerHTML = html

            if (resultsContentMatches) resultsContentMatches.style.display = 'block'
            if (resultsContentBottom) resultsContentBottom.style.display = 'block'
            if (resultsContentNoMatches) resultsContentNoMatches.style.display = 'none'
        } else {

            resultsList.style.display = 'none'
            resultsList.innerHTML = ''

            if (resultsContentMatches) resultsContentMatches.style.display = 'none'
            if (resultsContentBottom) resultsContentBottom.style.display = 'none'
            if (resultsContentNoMatches) resultsContentNoMatches.style.display = 'block'
        }

        if (config.applyLink && resultsViewAll) {
            const url = new URL(config.applyLink, window.location.origin)
            if (programme) url.searchParams.set('type', programme)
            if (config.location) url.searchParams.set('location', config.location)
            resultsViewAll.innerHTML = `
                <a target="_blank" class="button button--fill" href="${url.toString()}">View all ${config.applyTextMap[programme] ?? 'Programmes'}</a>
            `
        }
	})
}

function renderJob({ job, score }: MatchItem, index: number) {
    const { image, description } = job

    const isTopMatch = index === 0

    const cardClass = isTopMatch
        ? 'article-card article-card--inset article-card--horizontal thumb--hover article-card--pink shadow shadow--red skills-matcher__top-match'
        : 'article-card article-card--inset article-card--horizontal thumb--hover'

    const linkClass = isTopMatch 
        ? 'button button--small button--arrow button--arrow-red button--white' 
        : 'button button--small button--arrow'

    const mediaHtml = image 
        && `<img class="article-card__media-img object-fit" src="${image.src}" srcset="${image.srcset}" sizes="100vw" role="presentation" alt="${image.alt}" style="object-position:${image.x}% ${image.y}%">` || ''
    return `
        <li>
            <div class="${cardClass}">
                <div class="article-card__media thumb relative overflow-hide">
                    ${mediaHtml}
                </div>
                <div class="article-card__content first-last">
                    <h4 class="heading-4 article-card__heading slash">${job.title}</h4>
                    ${description ? `<p class="margin-top-0">${description}</p>` : ''}
                    <div class="article-card__footer">
                        <div class="article-card__footer__left"><a target="_blank" href="${job.url}" class="a11y-link ${linkClass}">Find out more</a></div>
                        <div class="article-card__footer__right">
                            ${isTopMatch ? 'Best match' : ''}
                        </div>
                    </div>
                </div>
            </div>
        </li>
    `
}


function toArray(value: string | string[] | undefined): string[] {
    return Array.isArray(value) ? value : (value ? [value] : [])
}

function jobQuestions(job: Job): Question[] {
    return Object.entries(job.scores).flatMap(([stageTitle, scores]) =>
        Object.keys(scores).map(label => ({ stageTitle, label }))
    )
}

function relevance(job: Job, questions: Question[]): number {
    return questions.reduce((total, question) => {
        const priority = job.scores[question.stageTitle]?.[question.label]
        return total + (priority > 0 ? 1 / priority : 0)
    }, 0)
}

function matchJobs(
    values: Values, 
    stages: Stage[], 
    jobs: Job[], 
    programme: string, 
    location: string
): MatchItem[] {

    const selected = stages.flatMap(stage =>
        toArray(values[stage.name]).map(label => ({ stageTitle: stage.title, label }))
    )

    let matches = jobs
        .map(job => {
            const match = relevance(job, selected)
            const full = relevance(job, jobQuestions(job))
            return { job, score: Math.round((match / (full || 1)) * 100) }
        })
        .sort((a, b) => b.score - a.score)

    if (programme) {
        matches = matches.filter(item => item.job.types.includes(programme))
    }

    if (location) {
        matches = matches.filter(item => item.job.locations.includes(location))
    }

    return matches
}